import PropTypes from "prop-types";
import { useState } from "react";
import {
  Grid,
  FormControl,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
  OutlinedInput,
  Select,
  MenuItem,
} from "@mui/material";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftBox from "components/SoftBox";
import { Controller } from "react-hook-form";

function getSelectedValue(selectedValues, name) {
  // Parse all key segments: "api[value]" → ["api", "value"]
  const keys = name.match(/([^\[\]]+)/g); // regex that pulls keys out of nested brackets

  if (!keys || keys.length === 0) return undefined;

  // Resolve value step-by-step
  return keys.reduce((acc, key) => acc?.[key], selectedValues);
}

function setNestedValue(obj, path, value) {
  const keys = path.match(/([^\[\]]+)/g); // e.g., "api[api_key]" → ["api", "api_key"]
  if (!keys || keys.length === 0) return obj;

  const updated = { ...obj }; // clone original
  let current = updated;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];

    // If current[key] doesn't exist or is not an object, replace it
    if (typeof current[key] !== "object" || current[key] === null) {
      current[key] = {};
    }

    current = current[key];
  }

  current[keys[keys.length - 1]] = value;
  return updated;
}

const getNestedError = (obj, path) => {
  const keys = path.match(/([^\[\]]+)/g); // converts "api[api_key]" to ["api", "api_key"]
  return keys?.reduce((acc, key) => acc?.[key], obj);
};

const AttributeField = ({
  fieldData,
  label,
  control,
  errors,
  setSelectedValues,
  selectedValues,
  register,
}) => {
  const attributeField = [typeof fieldData === "string" ? JSON.parse(fieldData) : fieldData];

  return (
    <>
      {attributeField.map((field, index) => (
        <RenderField
          key={index}
          fieldData={field}
          label={label}
          control={control}
          errors={errors}
          setSelectedValues={setSelectedValues}
          selectedValues={selectedValues}
          register={register}
        />
      ))}
    </>
  );
};

AttributeField.propTypes = {
  fieldData: PropTypes.any.isRequired,
  label: PropTypes.string.isRequired,
  control: PropTypes.object,
  errors: PropTypes.object,
  setSelectedValues: PropTypes.func,
  selectedValues: PropTypes.object,
  register: PropTypes.func,
};

const RenderField = ({
  fieldData,
  label,
  control,
  errors,
  setSelectedValues,
  selectedValues,
  register,
}) => {
  const { name, type, options } = fieldData;
  const selectedValue = getSelectedValue(selectedValues, name);

  const selectedOption = options?.find((opt) => opt.value === selectedValue);

  const handleChange = (name, event) => {
    const value = event.target.value;
    setSelectedValues((prevValues) => {
      const updated = { ...prevValues };
      return setNestedValue(updated, name, value);
    });
  };

  return (
    <>
      <Grid item xs={12} md={6} sx={{ mb: 2 }}>
        {type === "radio" && (
          <>
            <FormControl component="fieldset" mb={2}>
              <SoftTypography variant="overline" gutterBottom>
                {label}
              </SoftTypography>
              <Stack direction="row" gap={3}>
                <Controller
                  name={`attributes[${name}]`}
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => {
                        field.onChange(e.target.value); // ✅ update form state
                        handleChange(name, e);
                      }}
                      row
                    >
                      {options?.map((option, idx) => (
                        <div key={idx}>
                          <FormControlLabel
                            sx={{ marginLeft: 0 }}
                            value={option.value}
                            control={<Radio />}
                            label={
                              <SoftTypography variant="button" fontWeight="regular">
                                {option.label}
                              </SoftTypography>
                            }
                          />
                        </div>
                      ))}
                    </RadioGroup>
                  )}
                />
                {getNestedError(errors?.attributes, name) && (
                  <SoftTypography color="error" variant="caption">
                    {getNestedError(errors?.attributes, name)?.message}
                  </SoftTypography>
                )}
              </Stack>
            </FormControl>
          </>
        )}

        {type === "select" && (
          <SoftBox fullWidth>
            <SoftTypography variant="overline" gutterBottom>
              {label}
            </SoftTypography>
            <Controller
              name={`attributes[${name}]`}
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  value={field?.value || ""}
                  onChange={(e) => {
                    field.onChange(e.target.value); // update form state
                    handleChange(name, e);
                  }}
                  fullWidth
                  input={<OutlinedInput sx={{ height: 56 }} />}
                  sx={{ padding: "10px" }}
                  error={!!getNestedError(errors?.attributes, name)}
                >
                  <MenuItem key="" value="">
                    {t("lang.choose")}
                  </MenuItem>
                  {options?.map((option, idx) => (
                    <MenuItem key={idx} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            {getNestedError(errors?.attributes, name) && (
              <SoftTypography color="error" variant="caption">
                {getNestedError(errors?.attributes, name)?.message}
              </SoftTypography>
            )}
          </SoftBox>
        )}
        {["number", "text"].includes(type) && (
          <SoftBox>
            <SoftTypography variant="overline" gutterBottom>
              {label}
            </SoftTypography>
            <SoftInput
              {...register(`attributes[${name}]`, {
                required: fieldData?.required ? `${label} is required` : false,
              })}
              onChange={(e) => handleChange(name, e)}
              fullWidth
            />
            {getNestedError(errors?.attributes, name) && (
              <SoftTypography color="error" variant="caption">
                {getNestedError(errors?.attributes, name)?.message}
              </SoftTypography>
            )}
          </SoftBox>
        )}
      </Grid>
      {type === "radio" &&
        selectedOption?.fields?.length > 0 &&
        selectedOption.fields.map((field, idx) => (
          <RenderField
            key={`subfield-radio-${field.name}-${idx}`}
            fieldData={field}
            label={field.label || ""}
            control={control}
            errors={errors}
            setSelectedValues={setSelectedValues}
            selectedValues={selectedValues}
            register={register}
          />
        ))}
      {type === "select" && selectedOption?.field && (
        <RenderField
          key={`subfield-${selectedValue}`}
          fieldData={selectedOption.field}
          label={selectedOption.field.label || label}
          control={control}
          errors={errors}
          setSelectedValues={setSelectedValues}
          selectedValues={selectedValues}
          register={register}
        />
      )}
      {type === "select" &&
        selectedOption?.fields?.length > 0 &&
        selectedOption.fields.map((field, index) => (
          <RenderField
            key={`subfields-${field.name}-${index}`}
            fieldData={field}
            label={field.label || label}
            control={control}
            errors={errors}
            setSelectedValues={setSelectedValues}
            selectedValues={selectedValues}
            register={register}
          />
        ))}
      {/* {selectedOption?.eem_fields?.length > 0 &&
        selectedOption.eem_fields.map((eemField, index) =>
          Object.entries(eemField).map(([key, val]) => (
            <input
              key={`${index}-${key}`}
              type="hidden"
              {...register(`attributes[eem_fields][${selectedValue}][${index}][${key}]`)}
              value={String(val)}
            />
          ))
        )} */}

      {selectedOption?.eem_fields?.length > 0 &&
        renderEemFields(selectedOption.eem_fields, register, selectedValue)}
    </>
  );
};

const renderEemFields = (data, register, selectedValue, basePath = "") => {
  if (Array.isArray(data)) {
    return data.map((item, index) =>
      renderEemFields(item, register, selectedValue, `${basePath}[${index}]`)
    );
  }

  if (typeof data === "object" && data !== null) {
    return Object.entries(data).map(([key, val]) => {
      const currentPath = basePath ? `${basePath}[${key}]` : key;

      if (typeof val === "object" && val !== null) {
        // Recursive case: val is an object or array
        return renderEemFields(val, register, selectedValue, currentPath);
      } else {
        // Base case: val is a primitive value
        return (
          <input
            key={currentPath}
            type="hidden"
            {...register(
              `attributes[eem_fields][${selectedValue}]${currentPath ? `[${currentPath}]` : ""}`
            )}
            value={String(val)}
          />
        );
      }
    });
  }

  // If data is a primitive value
  return (
    <input
      key={basePath}
      type="hidden"
      {...register(`attributes[eem_fields][${selectedValue}]${basePath ? `[${basePath}]` : ""}`)}
      value={String(data)}
    />
  );
};

RenderField.propTypes = {
  fieldData: PropTypes.shape({
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    label: PropTypes.string,
    required: PropTypes.bool,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.any,
        field: PropTypes.object, // recursive
      })
    ),
  }).isRequired,
  label: PropTypes.string.isRequired,
  control: PropTypes.object,
  errors: PropTypes.object,
  setSelectedValues: PropTypes.func,
  selectedValues: PropTypes.object,
  register: PropTypes.func,
};

export default AttributeField;
